import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { clearGenders, loadGenderDetails } from '../../../store/directusService';
import { loadContractConstants, updateContractConstants } from '../../../store/participantService';
import { updatePreSelects, updateSelectedItems } from '../../../utils/directusFunctions';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    CONTRACT_CONSTANTS_ERROR,
    GENDER_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validatePrimaryDetailsAdmin';

import form from '../../../commonStyles/formStyles.module.css';
import admin from '../adminStyles.module.css';

const viewRoles = [LOCAL_ADMIN, SUPERUSER];
const acceptedRoles = [SUPERUSER];
const collectionName = 'gender';
const urlName = 'gender';

const PrimaryDetailsAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        temporaryUniqueIdAvailable: false,
        allowDuplicateUniqueIds: false,
        contactCentreCreateScreenUseTemporaryUniqueId: false,
        contactCentreCreateScreenPostCodeLookup: false
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialEntryState, setInitialEntryState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [selectedGenders, setSelectedGenders] = useState([]);
    const [preSelectedGenderIds, setPreSelectedGenderIds] = useState([]);
    const [preSelectedGenders, setPreSelectedGenders] = useState([]);
    const [keys, setKeys] = useState({ selectedGenders: '0' });
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const genderDetails = useSelector((state) => state.entities.directusService.genderDetails);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!contractId) return;
        contractSetup();
        if (!Object.keys(contractConstants).length || contractConstants.contractId !== contractId) {
            dispatch(loadContractConstants(contractId));
        }
    }, [contractId]);

    useEffect(() => {
        if (genderDetails?.length > 0) {
            const { preSelects, preSelectIds } = updatePreSelects(
                collectionName,
                genderDetails,
                contractId
            );
            setPreSelectedGenders(preSelects);
            setPreSelectedGenderIds(preSelectIds);
        }
    }, [genderDetails]);

    useEffect(() => {
        if (Object.keys(contractConstants).length > 0) {
            const entries = {
                temporaryUniqueIdAvailable: contractConstants.temporaryUniqueIdAvailable || false,
                allowDuplicateUniqueIds: contractConstants.allowDuplicateUniqueIds || false,
                contactCentreCreateScreenUseTemporaryUniqueId:
                    contractConstants.contactCentreCreateScreenUseTemporaryUniqueId || false,
                contactCentreCreateScreenPostCodeLookup:
                    contractConstants.contactCentreCreateScreenPostCodeLookup || false
            };
            setNewEntry(entries);
            setInitialEntryState(entries);
        }
    }, [contractConstants]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes('Updating gender collection')) {
            dispatch(clearGenders());
            dispatch(loadGenderDetails());
        }
        setIsButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const contractSetup = () => {
        setIsButtonDisabled(false);
        setSelectedGenders([]);
        setPreSelectedGenders([]);
        setNewEntry(initialState);
        setErrors({});
        setKeys(clearKeys(keys));
        dispatch(clearGenders());
        dispatch(loadGenderDetails());
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (genderDetails?.length < 1) return GENDER_ERROR;
        if (!Object.keys(contractConstants).length) return CONTRACT_CONSTANTS_ERROR;
    };

    // EVENT HANDLERS
    const onGenderChange = (chosenIds) => {
        setIsButtonDisabled(false);
        clearError('genderDetails');
        if (!chosenIds) chosenIds = [];
        setSelectedGenders(chosenIds);
    };

    const onUpdate = (key, value) => {
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);

        const { newErrors, isGendersChanged, isConstantsChanged } = validate(
            selectedGenders,
            preSelectedGenderIds,
            newEntry,
            initialEntryState
        );
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        isGendersChanged &&
            updateSelectedItems(
                urlName,
                genderDetails,
                selectedGenders,
                contractId,
                collectionName,
                dispatch
            );
        isConstantsChanged &&
            dispatch(updateContractConstants({ ...contractConstants, ...newEntry }));
    };

    // AWAITING CONTENT
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <form
            onSubmit={onSubmit}
            className={form.formWrapper}
            data-testid="primary_details_admin_form_start">
            <MultiSelect
                id="genderDetails"
                key={keys.selectedGenders}
                label="Gender"
                placeholder="Select genders..."
                disabled={!hasRole(acceptedRoles, roles)}
                mandatory={true}
                menuItems={genderDetails || []}
                preSelectedIds={preSelectedGenderIds}
                preSelects={preSelectedGenders}
                error={errors.genderDetails}
                onChange={(chosenIds) => onGenderChange(chosenIds)}
            />
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <RadioButtons
                        id="temporaryUniqueIdAvailable"
                        label="Temporary Unique ID Available"
                        value={newEntry.temporaryUniqueIdAvailable}
                        onChange={(value) => onUpdate('temporaryUniqueIdAvailable', value)}
                    />
                    <RadioButtons
                        id="allowDuplicateUniqueIds"
                        label="Allow Duplicate Unique IDs"
                        value={newEntry.allowDuplicateUniqueIds}
                        onChange={(value) => onUpdate('allowDuplicateUniqueIds', value)}
                    />
                </div>
                <div className={form.formColumn}>
                    <RadioButtons
                        id="contactCentreCreateScreenUseTemporaryUniqueId"
                        label="Contact Centre Create Screen Use Temporary Unique ID"
                        value={newEntry.contactCentreCreateScreenUseTemporaryUniqueId}
                        disabled={!hasRole(acceptedRoles, roles)}
                        onChange={(value) => onUpdate('contactCentreCreateScreenUseTemporaryUniqueId', value)}
                    />
                    <RadioButtons
                        id="contactCentreCreateScreenPostCodeLookup"
                        label="Contact Centre Create Screen Postcode Lookup"
                        value={newEntry.contactCentreCreateScreenPostCodeLookup}
                        disabled={!hasRole(acceptedRoles, roles)}
                        onChange={(value) => onUpdate('contactCentreCreateScreenPostCodeLookup', value)}
                    />
                </div>
            </div>
            <Button
                id="primaryDetailsAdmin"
                content="UPDATE PRIMARY DETAILS"
                disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                customErrorClass="reduceTopMargin"
                error={errors.button}
                clearError={() => clearError('button')}
            />
            <div className={admin.titlesBlockWrapper}>
                <p>First Name</p>
                <p>Middle Name</p>
                <p>Last Name</p>
                <p>Preferred Name</p>
                <p>Preferred Pronouns</p>
                <p>Date of Birth</p>
                <p>Address line 1</p>
                <p>Address line 2</p>
                <p>Address line 3</p>
                <p>Town/City</p>
                <p>Postcode</p>
                <p>Care of Address</p>
                <p>Primary Phone Number</p>
                <p>Secondary Phone Number</p>
                <p>Email Address</p>
                <p>Unique Identifier</p>
                <p>Eligibility Groups DDL</p>
                <p>Referral Sources DDL</p>
                <p>Referral Date</p>
                <p>Start Date</p>
                <p>Adviser</p>
                <p>Current Status</p>
                <p>Preferred method of contact</p>
                <p>Possible Good News Story?</p>
            </div>
        </form>
    );
};

PrimaryDetailsAdmin.propTypes = {
    contractId: PropTypes.string
};

export default PrimaryDetailsAdmin;
